import { IDiscounts } from "modules/order/domain/models/IDiscounts";
import { IOrder } from "modules/order/domain/models/IOrder";

export function applyDiscountsToOrder(
  order: IOrder,
  eventId: string,
  discountsFromServer: IDiscounts[]
): IOrder | null {
  const updatedOrder: IOrder = {
    ...order,
    orderItems: order.orderItems.map((it) => ({ ...it })),
    superAppEventId: eventId,
  };

  for (const item of updatedOrder.orderItems) {
    item.discount = 0;
    item.discountQuantity = 0;
  }

  const discountsByProductId = new Map<string, Array<{ discountId: string; perUnitDiscount: number; availableUses: number }>>();

  for (const d of discountsFromServer) {
    const availableUses = Math.max(0, d.maxUses - d.used);
    if (availableUses <= 0) continue;

    for (const p of d.products) {
      const arr = discountsByProductId.get(p.productId) ?? [];
      arr.push({
        discountId: d.id,
        perUnitDiscount: p.discount,
        availableUses,
      });
      discountsByProductId.set(p.productId, arr);
    }
  }

  // -------- 4) Identifica candidatos a desconto --------
  type Candidate = {
    orderItemIndex: number;
    productId: string;
    perUnitDiscount: number;
    remainingItemUnits: number;
    discountId: string;
  };

  const candidates: Candidate[] = [];

  updatedOrder.orderItems.forEach((item, idx) => {
    const possibleDiscounts = discountsByProductId.get(item.productId);
    if (!possibleDiscounts?.length) return;

    const best = [...possibleDiscounts].sort((a, b) => b.perUnitDiscount - a.perUnitDiscount)[0];

    // Unidades elegíveis = quantity do item.
    candidates.push({
      orderItemIndex: idx,
      productId: item.productId,
      perUnitDiscount: best.perUnitDiscount,
      remainingItemUnits: Math.max(0, item.quantity),
      discountId: best.discountId,
    });
  });

  // -------- 5) Ordena candidatos pela estratégia --------
  candidates.sort((a, b) => b.perUnitDiscount - a.perUnitDiscount);

  // -------- 6) Controla usos restantes por descontoId --------
  const remainingUsesByDiscountId = new Map<string, number>();
  for (const d of discountsFromServer) {
    remainingUsesByDiscountId.set(d.id, Math.max(0, d.maxUses - d.used));
  }

  let anyDiscountApplied = false;

  // -------- 7) Distribui usos: consome usos e aplica desconto nos itens --------
  for (const c of candidates) {
    const usesLeft = remainingUsesByDiscountId.get(c.discountId) ?? 0;
    if (usesLeft <= 0) continue;

    if (c.remainingItemUnits <= 0) continue;

    // quantas unidades desse item vamos descontar,
    // limitado tanto pelo qty do item quanto pelo limite de usos do desconto
    const unitsToDiscount = Math.min(c.remainingItemUnits, usesLeft);

    if (unitsToDiscount <= 0) continue;

    const item = updatedOrder.orderItems[c.orderItemIndex];

    item.unitDiscount = c.perUnitDiscount;

    const discountValue = c.perUnitDiscount * unitsToDiscount;

    item.discount = (item.discount ?? 0) + discountValue;

    item.discountId = c.discountId;

    // Quantas unidades deste item receberam desconto
    item.discountQuantity = (item.discountQuantity ?? 0) + unitsToDiscount;

    anyDiscountApplied = true;

    // Atualiza usos restantes do desconto
    remainingUsesByDiscountId.set(c.discountId, usesLeft - unitsToDiscount);

    // Atualiza unidades restantes do item
    c.remainingItemUnits -= unitsToDiscount;
  }

  if (!anyDiscountApplied) {
    return null;
  }

  // -------- 8) Recalcula totais do pedido --------
  const totalWithDiscount = updatedOrder.orderItems.reduce((sum, item) => {
    const itemTotal = (item.totalPrice * item.quantity) - (item?.discount ?? 0);

    return sum + itemTotal;
  }, 0);

  updatedOrder.totalValue = totalWithDiscount;

  return updatedOrder;
}
